import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Phone, Mail, Calendar, Clock, ChevronRight } from 'lucide-react';
import SystemDataService from '../../Services/SystemDataService.js';
import { getMapsData } from '../../Public/maps-data.js';
import MapFrame from '../Map/map-frame-component.js';

const ACTION_ICONS = { phone: Phone, mail: Mail, calendar: Calendar };

/**
 * Contact / service-area section, an accent two-line heading, a left "contact
 * card" (response time + quick actions + business hours) and the interactive
 * MapFrame. Content is data-driven; business hours come from businessInfo.
 */
const ENGLISH_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MapSection = () => {
    const systemData = useSelector((state) => state.systemData || {});
    const { language = 'he', dir, links = [], businessInfo = {} } = systemData;

    const BusinessMaps = SystemDataService.byLabel(links, 'Business Maps');
    const phone = SystemDataService.byLabel(links, 'Phone');
    const whatsapp = SystemDataService.byLabel(links, 'WhatsApp') || SystemDataService.byLabel(links, 'Whatsapp');

    const data = useMemo(() => getMapsData(language), [language]);
    const { heading = {} } = data;

    // Business hours come from businessInfo (per-day keys: Sunday…Saturday). Only
    // days with a value are shown.
    const hourRows = ENGLISH_DAYS
        .map((day) => ({ day, value: businessInfo[day] }))
        .filter((r) => r.value);

    // Resolve each quick-action's href from the available links, falling back to
    // the on-page contact section.
    const hrefFor = (key) => {
        if (key === 'call') return phone?.url || '/#contact';
        if (key === 'message') return whatsapp?.url || '/#contact';
        if (key === 'schedule') {
            return SystemDataService.byLabel(links, 'Schedule')?.url
                || SystemDataService.byLabel(links, 'Calendly')?.url
                || '/#contact';
        }
        return '/#contact';
    };

    return (
        <section dir={dir} className="w-full bg-[#F6F7F9] py-[72px] px-4 sm:px-6 lg:px-10">
            <div className="mx-auto w-full max-w-[1300px]">

                {/* Header */}
                <div className="max-w-2xl text-start">
                    <h2 className="font-secondary text-[36px] sm:text-[52px] font-extrabold leading-[1.05] tracking-[-0.02em] text-primary-500">
                        {heading.pre}{' '}
                        <span className="text-secondary-500">{heading.accent}</span>{' '}
                        {heading.post}
                    </h2>
                    {data.subtitle && (
                        <p className="mt-4 max-w-xl text-lg leading-relaxed text-primary-300">{data.subtitle}</p>
                    )}
                </div>

                {/* Card + Map */}
                <div className="mt-10 grid items-stretch gap-6 lg:grid-cols-12">

                    {/* Left: contact card */}
                    <div className="flex flex-col overflow-hidden rounded-[24px] border border-primary-100 bg-white shadow-sm lg:col-span-5">
                        <div className="flex flex-1 flex-col p-7 sm:p-8">

                            {/* Response time */}
                            <div className="flex items-start gap-4">
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary-500/10 text-secondary-500">
                                    <Clock className="h-6 w-6" strokeWidth={2} />
                                </span>
                                <div>
                                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary-300">
                                        {data.responseEyebrow}
                                    </div>
                                    <div className="mt-1 font-secondary text-2xl font-bold leading-tight text-primary-500">
                                        {data.responseValue}
                                    </div>
                                </div>
                            </div>

                            {/* Quick actions */}
                            <div className="mt-6 border-t border-primary-100">
                                {data.actions?.map((action) => {
                                    const Icon = ACTION_ICONS[action.icon] || Phone;
                                    return (
                                        <a
                                            key={action.key}
                                            href={hrefFor(action.key)}
                                            className="group flex items-center gap-4 border-b border-primary-100 py-4 transition-colors last:border-b-0"
                                        >
                                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-500 transition-colors group-hover:bg-secondary-500 group-hover:text-white">
                                                <Icon className="h-5 w-5" strokeWidth={2} />
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block font-bold text-primary-500">{action.title}</span>
                                                <span className="block text-sm text-primary-300">{action.subtitle}</span>
                                            </span>
                                            <ChevronRight className="h-5 w-5 shrink-0 text-primary-200 transition-colors group-hover:text-secondary-500 rtl:rotate-180" strokeWidth={2.5} />
                                        </a>
                                    );
                                })}
                            </div>

                            {/* Business hours, sourced from businessInfo */}
                            {hourRows.length > 0 && (
                                <div className="mt-6">
                                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary-300">
                                        {data.hoursTitle}
                                    </div>
                                    <div className="mt-3 space-y-2">
                                        {hourRows.map((row) => (
                                            <div key={row.day} className="flex items-center justify-between gap-4 text-[15px]">
                                                <span className="font-bold text-primary-500">{row.day}</span>
                                                <span className="text-primary-400">{row.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: map */}
                    <div className="relative min-h-[440px] overflow-hidden rounded-[24px] border border-primary-100 shadow-lg lg:col-span-7">
                        <MapFrame url={BusinessMaps?.url || data.mapEmbedUrl} customData={data} className="h-full w-full" interactive={false} />
                    </div>

                </div>
            </div>
        </section>
    );
};

export default MapSection;
